function ImpactStories() {
    try {
        const stories = [
            {
                id: 1,
                title: "Transforming Education in Rural Kenya",
                description: "How refurbished computers are enabling digital literacy in remote areas",
                image: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
                quote: "The computers have opened up a whole new world of opportunities for our students.",
                author: "John Kamau",
                role: "School Principal",
                stats: {
                    students: 450,
                    computers: 25,
                    co2Saved: 15
                }
            },
            {
                id: 2,
                title: "Bridging the Digital Divide in London",
                description: "Supporting inner-city schools with technology access",
                image: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
                quote: "Our students now have the tools they need to prepare for the digital future.",
                author: "Sarah Thompson",
                role: "IT Coordinator",
                stats: {
                    students: 850,
                    computers: 45,
                    co2Saved: 30
                }
            }
        ];

        React.useEffect(() => {
            async function createVoxelStoriesVisualizations() {
                try {
                    // Wait for the DOM to be ready
                    if (document.readyState === 'loading') {
                        await new Promise(resolve => {
                            document.addEventListener('DOMContentLoaded', resolve);
                        });
                    }
                    
                    // Import Three.js
                    const THREE = await import('three');
                    
                    const cleanupFunctions = [];
                    
                    // Create a voxel representation for each story
                    stories.forEach((story, storyIndex) => {
                        const containerId = `story-voxels-${story.id}`;
                        const container = document.getElementById(containerId);
                        
                        if (!container) return;
                        
                        // Create scene
                        const scene = new THREE.Scene();
                        scene.background = new THREE.Color(0x111827);
                        
                        // Create camera
                        const camera = new THREE.PerspectiveCamera(
                            60,
                            container.clientWidth / container.clientHeight,
                            0.1,
                            1000
                        );
                        camera.position.set(0, 1.5, 3);
                        camera.lookAt(0, 0, 0);
                        
                        // Create renderer with transparency
                        const renderer = new THREE.WebGLRenderer({
                            antialias: true,
                            alpha: true
                        });
                        renderer.setSize(container.clientWidth, container.clientHeight);
                        renderer.setClearColor(0x000000, 0); // Transparent
                        container.appendChild(renderer.domElement);
                        
                        // Add lights
                        const ambientLight = new THREE.AmbientLight(0x404040, 1);
                        scene.add(ambientLight);
                        
                        const directionalLight = new THREE.DirectionalLight(0x10b981, 0.8);
                        directionalLight.position.set(1, 2, 1);
                        scene.add(directionalLight);
                        
                        // Create voxel grid based on the story's stats
                        const voxels = [];
                        
                        // Create a bar chart representation with voxels
                        const barWidth = 0.2;
                        const barGap = 0.3;
                        const maxBarHeight = 2;
                        
                        // Scale factor to make visual heights proportional
                        const maxStatValue = Math.max(
                            story.stats.students / 100,
                            story.stats.computers * 2,
                            story.stats.co2Saved * 3
                        );
                        
                        const createBar = (value, position, color) => {
                            // Normalize height based on the maximum value
                            const normalizedHeight = (value / maxStatValue) * maxBarHeight;
                            const segments = Math.ceil(normalizedHeight / barWidth);
                            
                            for (let i = 0; i < segments; i++) {
                                const voxelGeometry = new THREE.BoxGeometry(barWidth, barWidth, barWidth);
                                const voxelMaterial = new THREE.MeshStandardMaterial({
                                    color: typeof color === 'function' ? color(i / segments) : color,
                                    metalness: 0.3,
                                    roughness: 0.7,
                                    emissive: typeof color === 'function' ? color(i / segments) : color,
                                    emissiveIntensity: 0.2
                                });
                                
                                const voxel = new THREE.Mesh(voxelGeometry, voxelMaterial);
                                
                                // Position in a vertical stack
                                voxel.position.set(
                                    position.x,
                                    position.y + (i * barWidth),
                                    position.z
                                );
                                
                                scene.add(voxel);
                                voxels.push({
                                    mesh: voxel,
                                    initialY: voxel.position.y,
                                    phaseOffset: Math.random() * Math.PI * 2
                                });
                            }
                        };
                        
                        // Create three bars representing the three stats
                        createBar(
                            story.stats.students / 100, 
                            { x: -barGap, y: 0, z: 0 },
                            i => new THREE.Color(0x10b981).lerp(new THREE.Color(0x047857), i)
                        );
                        
                        createBar(
                            story.stats.computers * 2,
                            { x: 0, y: 0, z: 0 },
                            i => new THREE.Color(0x3b82f6).lerp(new THREE.Color(0x1e40af), i)
                        );
                        
                        createBar(
                            story.stats.co2Saved * 3,
                            { x: barGap, y: 0, z: 0 },
                            i => new THREE.Color(0x6366f1).lerp(new THREE.Color(0x4338ca), i)
                        );
                        
                        // Add floating voxel icons for labels
                        const iconSize = 0.15;
                        const iconHeight = -0.3;
                        
                        const createIconVoxel = (position, color) => {
                            const iconGeometry = new THREE.BoxGeometry(iconSize, iconSize, iconSize);
                            const iconMaterial = new THREE.MeshStandardMaterial({
                                color: color,
                                emissive: color,
                                emissiveIntensity: 0.3
                            });
                            
                            const icon = new THREE.Mesh(iconGeometry, iconMaterial);
                            icon.position.set(position.x, position.y, position.z);
                            scene.add(icon);
                            
                            return icon;
                        };
                        
                        // Add label icons
                        createIconVoxel({ x: -barGap, y: iconHeight, z: 0 }, 0x10b981); // Students
                        createIconVoxel({ x: 0, y: iconHeight, z: 0 }, 0x3b82f6);       // Computers
                        createIconVoxel({ x: barGap, y: iconHeight, z: 0 }, 0x6366f1);  // CO2
                        
                        // Animation loop
                        function animate() {
                            requestAnimationFrame(animate);
                            
                            const time = Date.now() * 0.001;
                            
                            // Add subtle movement to each voxel
                            voxels.forEach((voxel, i) => {
                                const hover = Math.sin(time + voxel.phaseOffset) * 0.03;
                                voxel.mesh.position.y = voxel.initialY + hover;
                                
                                // Subtle rotation
                                voxel.mesh.rotation.y = Math.sin(time * 0.3 + i * 0.1) * 0.1;
                            });
                            
                            renderer.render(scene, camera);
                        }
                        
                        // Handle window resize
                        function onResize() {
                            if (container) {
                                camera.aspect = container.clientWidth / container.clientHeight;
                                camera.updateProjectionMatrix();
                                renderer.setSize(container.clientWidth, container.clientHeight);
                            }
                        }
                        
                        window.addEventListener('resize', onResize);
                        animate();
                        
                        // Store cleanup function
                        cleanupFunctions.push(() => {
                            window.removeEventListener('resize', onResize);
                            if (container && container.contains(renderer.domElement)) {
                                container.removeChild(renderer.domElement);
                            }
                            
                            // Dispose geometries and materials
                            voxels.forEach(voxel => {
                                if (voxel.mesh.geometry) voxel.mesh.geometry.dispose();
                                if (voxel.mesh.material) voxel.mesh.material.dispose();
                            });
                        });
                    });
                    
                    // Return main cleanup function
                    return () => {
                        cleanupFunctions.forEach(cleanup => cleanup());
                    };
                } catch (error) {
                    console.error('Error creating voxel story visualizations:', error);
                }
            }
            
            const cleanupPromise = createVoxelStoriesVisualizations();
            return () => {
                cleanupPromise.then(cleanup => cleanup && cleanup());
            };
        }, []);

        return (
            <section className="py-24 bg-gray-900">
                <div className="container mx-auto px-4">
                    <h2 className="text-4xl font-bold text-center mb-16 text-white">
                        Impact <span className="text-green-400">Stories</span>
                    </h2>
                    <div className="space-y-32">
                        {stories.map((story, index) => (
                            <div 
                                key={story.id}
                                className="group"
                            >
                                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                                    index % 2 === 0 ? '' : 'lg:flex-row-reverse'
                                }`}>
                                    <div className="relative">
                                        <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden shadow-xl transform transition-transform duration-500 group-hover:shadow-[0_10px_40px_rgba(16,185,129,0.3)]">
                                            <img 
                                                src={story.image}
                                                alt={story.title}
                                                className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-105"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
                                        </div>
                                        
                                        {/* Voxel 3D Chart */}
                                        <div 
                                            id={`story-voxels-${story.id}`}
                                            className="absolute h-32 w-32 lg:h-40 lg:w-40 -top-16 -right-8 lg:-top-20 lg:-right-12 transform transition-transform duration-500 group-hover:-translate-y-2 pointer-events-none"
                                            aria-hidden="true"
                                        ></div>
                                        
                                        <div className="absolute bottom-0 left-0 right-0 p-6">
                                            <div className="grid grid-cols-3 gap-4 text-white">
                                                <div className="text-center bg-gray-800/60 backdrop-blur-sm rounded-lg p-2 transition-transform duration-300 transform group-hover:-translate-y-1">
                                                    <div className="text-2xl font-bold text-green-400">
                                                        {story.stats.students}
                                                    </div>
                                                    <div className="text-sm">Students</div>
                                                </div>
                                                <div className="text-center bg-gray-800/60 backdrop-blur-sm rounded-lg p-2 transition-transform duration-300 transform group-hover:-translate-y-1">
                                                    <div className="text-2xl font-bold text-blue-400">
                                                        {story.stats.computers}
                                                    </div>
                                                    <div className="text-sm">Computers</div>
                                                </div>
                                                <div className="text-center bg-gray-800/60 backdrop-blur-sm rounded-lg p-2 transition-transform duration-300 transform group-hover:-translate-y-1">
                                                    <div className="text-2xl font-bold text-indigo-400">
                                                        {story.stats.co2Saved}kg
                                                    </div>
                                                    <div className="text-sm">CO₂ Saved</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-6">
                                        <h3 className="text-3xl font-bold text-white group-hover:text-green-400 transition-colors duration-300">{story.title}</h3>
                                        <p className="text-gray-400">{story.description}</p>
                                        <blockquote className="border-l-4 border-green-400 pl-4 my-8 bg-gray-800/30 p-6 rounded-r-xl backdrop-blur-sm transform transition-transform duration-500 group-hover:-translate-y-1 group-hover:shadow-[0_5px_15px_rgba(16,185,129,0.2)]">
                                            <p className="text-xl text-gray-300 italic">{story.quote}</p>
                                            <footer className="mt-4 flex items-center">
                                                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center mr-4">
                                                    <i className="fas fa-quote-left text-green-400"></i>
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-white">{story.author}</div>
                                                    <div className="text-green-400">{story.role}</div>
                                                </div>
                                            </footer>
                                        </blockquote>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    } catch (error) {
        console.error('ImpactStories error:', error);
        reportError(error);
        return null;
    }
}

window.ImpactStories = ImpactStories;

export default ImpactStories;
